import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { normalizeQuarterlyActuals, calculateIkrProgress } from '../../lib/utils';
import KPIProgressBar from '../../components/ui/KPIProgressBar';

export default function IndividualKrRow({ ikr, onUpdate, users, teams, onDelete, isLocked, viewer, selectedTeamId }) {
  const normalizedIkr = { ...ikr, quarterlyActuals: normalizeQuarterlyActuals(ikr.quarterlyActuals) };
  const [ikrState, setIkrState] = useState(normalizedIkr);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [isOwnerListVisible, setIsOwnerListVisible] = useState(false);
  const ownerInputRef = useRef(null);

  const isSameVertical = (userTeamId, targetTeamId) => {
    if (!userTeamId || !targetTeamId) return false;
    return userTeamId.split('.')[0] === targetTeamId.split('.')[0];
  };

  const canEditFields = viewer.role === 'Admin' || viewer.role === 'L1 Team Lead' || viewer.role === 'L2 Team Lead';
  const canEditActuals = (() => {
    if (viewer.role === 'Admin') return true;
    if (viewer.uid === ikr.ownerId) return true;
    if (viewer.role === 'L1 Team Lead' && selectedTeamId) return isSameVertical(viewer.teamId?.trim(), selectedTeamId);
    if (viewer.role === 'L2 Team Lead' && selectedTeamId) return viewer.teamId?.trim() === selectedTeamId;
    return false;
  })();

  useEffect(() => {
    setIkrState({ ...ikr, quarterlyActuals: normalizeQuarterlyActuals(ikr.quarterlyActuals) });
  }, [ikr]);

  useEffect(() => {
    const owner = users.find(u => u.id === ikr.ownerId);
    setOwnerSearch(owner ? owner.name : '');
  }, [ikr.ownerId, users]);

  const filteredUsers = useMemo(() => {
    if (!ownerSearch) return users;
    return users.filter(u => u.name.toLowerCase().includes(ownerSearch.toLowerCase()));
  }, [ownerSearch, users]);

  const handleLocalUpdate = (field, value) => setIkrState(prev => ({ ...prev, [field]: value }));
  const triggerUpdate = () => onUpdate(ikr.id, ikrState);

  const handleOwnerSelect = (user) => {
    setOwnerSearch(user.name);
    const ownerId = user.uid || user.id;
    const newState = { ...ikrState, ownerId, teamId: user.teamId };
    setIkrState(newState);
    onUpdate(ikr.id, newState);
    setIsOwnerListVisible(false);
  };

  const progress = calculateIkrProgress(ikrState);

  return (
    <div className="py-2 border-b border-gray-200 last:border-b-0">
      <div className="flex gap-2 items-center" style={{ width: '1005px' }}>
        <div style={{ width: '220px', flexShrink: 0 }}>
          <input type="text" value={ikrState.text} onChange={(e) => handleLocalUpdate('text', e.target.value)} onBlur={triggerUpdate} className={`w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${!canEditFields ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} placeholder="Enter individual task..." disabled={!canEditFields} />
        </div>
        <div style={{ width: '120px', flexShrink: 0 }} className="relative" ref={ownerInputRef}>
          <input type="text" value={ownerSearch} onChange={(e) => { setOwnerSearch(e.target.value); setIsOwnerListVisible(true); }} onFocus={() => setIsOwnerListVisible(true)} onBlur={() => setTimeout(() => setIsOwnerListVisible(false), 200)} placeholder="Owner" className={`w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${!canEditFields ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} disabled={!canEditFields} />
          {isOwnerListVisible && filteredUsers.length > 0 && (
            <ul className="absolute z-20 bg-white border border-gray-200 rounded-lg mt-1 w-48 max-h-48 overflow-y-auto shadow-lg">
              {filteredUsers.map(user => (
                <li key={user.id} onMouseDown={() => handleOwnerSelect(user)} className="p-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0">{user.name}</li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ width: '100px', flexShrink: 0 }}>
          <select value={ikrState.teamId || ''} onChange={(e) => handleLocalUpdate('teamId', e.target.value)} onBlur={triggerUpdate} className={`w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 ${!canEditFields ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} disabled={!canEditFields}>
            <option value="" disabled>Team</option>
            {Object.entries(teams).map(([id, teamData]) => <option key={id} value={id}>{teamData.name}</option>)}
          </select>
        </div>
        <div style={{ width: '55px', flexShrink: 0 }}>
          <input type="number" value={ikrState.kpiTarget || ''} placeholder="Tgt" onChange={(e) => handleLocalUpdate('kpiTarget', parseFloat(e.target.value) || 0)} onBlur={triggerUpdate} className={`w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 ${!canEditFields ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} disabled={!canEditFields} />
        </div>
        <div style={{ width: '60px', flexShrink: 0 }}>
          <input type="text" value={ikrState.kpiUnit} placeholder="Unit" onChange={(e) => handleLocalUpdate('kpiUnit', e.target.value)} onBlur={triggerUpdate} className={`w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 ${!canEditFields ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} disabled={!canEditFields} />
        </div>
        <div style={{ width: '65px', flexShrink: 0 }}>
          <select value={ikrState.calculationMethod || 'sum'} onChange={(e) => { handleLocalUpdate('calculationMethod', e.target.value); onUpdate(ikr.id, { ...ikrState, calculationMethod: e.target.value }); }} className={`w-full p-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 ${!canEditFields ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} disabled={!canEditFields} title="Sum / Avg / Dev">
            <option value="sum">Sum</option>
            <option value="average">Avg</option>
            <option value="deviation">Dev</option>
          </select>
        </div>
        <div className="flex gap-1" style={{ width: '240px', flexShrink: 0 }}>
          {[0, 1, 2, 3].map(i => (
            <input key={i} type="number" value={ikrState.quarterlyActuals?.[i] ?? ''} onChange={(e) => { const newActuals = [...(ikrState.quarterlyActuals || [null, null, null, null])]; const v = e.target.value; newActuals[i] = v === '' ? null : (isNaN(parseFloat(v)) ? null : parseFloat(v)); handleLocalUpdate('quarterlyActuals', newActuals); }} onBlur={triggerUpdate} className={`w-full p-1.5 text-center border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-blue-500 ${!canEditActuals ? 'bg-gray-50 text-gray-500' : 'bg-white'}`} disabled={!canEditActuals} placeholder={`Q${i + 1}`} />
          ))}
        </div>
        <div className="flex items-center" style={{ width: '100px', flexShrink: 0 }}>
          <div className="flex-1"><KPIProgressBar value={progress} /></div>
          <span className="text-xs font-semibold ml-1 w-10 text-right">{(progress * 100).toFixed(0)}%</span>
        </div>
        <div className="flex justify-center" style={{ width: '30px', flexShrink: 0 }}>
          {viewer.role === 'Admin' && (
            <button onClick={() => onDelete(ikr.id)} className="text-gray-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-colors" title="Delete this task">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
